import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Job, JobStatus } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { isJobInPast } from '@core/util/job.util';
import {
  formatDate,
  getJobEndTimeString12h,
  getJobStartTimeString12h,
} from '@/utils/utils';
import { stripQuestionnaireDescriptionAddendum } from '@/utils/questionnaireDescription.util';
import {
  getJobDisplayScheduleEndDay,
  getJobDisplayScheduleStartDay,
} from '@core/util/jobScheduleBounds.util';
import { getWorkingDaysBadgesForJob } from '@core/job/workingDays/workingDays.util';
import Button from '@/components/Button';
import CancelJobModal from '@/components/modals/CancelJobModal';
import JobCancelInfo from './JobCancelInfo';
import WorkingDaysDisplay from './WorkingDaysDisplay';
import QuestionnaireResponses from './QuestionnaireResponses';
import AssignedWorker from './jobDrawer/workerSection/workerSelection/assignedWorker/AssignedWorker';
import WorkerSearchbar from './jobDrawer/workerSection/workerSelection/workerSearchbar/WorkerSearchbar';

interface JobDetailsProps {
  job: Job;
  onWorkerAssigned?: () => void | Promise<Job | null>;
  onWorkerUnassigned?: () => void | Promise<unknown>;
  onEdit: () => void;
  onCancelSuccess?: () => void;
  showWorkerLink?: boolean;
  canAssignWorker?: boolean;
  canEditJob?: boolean;
  canEditFilledJob?: boolean;
  showAssignedCandidateEmail?: boolean;
  showAssignedCandidatePhone?: boolean;
}

const CANCELLABLE_STATUSES = [JobStatus.Unfilled, JobStatus.Filled];

const JobDetails: React.FC<JobDetailsProps> = ({
  job,
  onWorkerAssigned,
  onWorkerUnassigned,
  onEdit,
  onCancelSuccess,
  showWorkerLink = true,
  canAssignWorker = true,
  canEditJob = true,
  canEditFilledJob = true,
  showAssignedCandidateEmail = false,
  showAssignedCandidatePhone = false,
}: JobDetailsProps) => {
  const fetchJobPostingQuestionnaire = useJobStore(
    (state) => state.fetchJobPostingQuestionnaire,
  );
  const jobPostingQuestionnaire = useJobStore(
    (state) => state.jobPostingQuestionnaire,
  );

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isAssigningWorker, setIsAssigningWorker] = useState(false);
  const { getTerm } = useTemplateStore();

  useEffect(() => {
    if (!job?.tenantId || !job?.customAttributes?.length) return;
    void fetchJobPostingQuestionnaire(job.tenantId);
  }, [
    job?.tenantId,
    job?.customAttributes?.length,
    fetchJobPostingQuestionnaire,
  ]);

  const questionnaireQuestions = useMemo(
    () => jobPostingQuestionnaire?.questions || null,
    [jobPostingQuestionnaire?.questions],
  );

  const isFutureJob = dayjs(Number(job?.startDate)).isAfter(dayjs());
  const isUnfilledOrDraft =
    job.itemStatus === JobStatus.Unfilled || job.itemStatus === JobStatus.Draft;
  // Clients (canEditFilledJob=false): only future Unfilled/Draft.
  // Tenants may also edit Filled jobs (past or future).
  const showEditButton = canEditFilledJob
    ? canEditJob &&
      ((isUnfilledOrDraft && isFutureJob) ||
        job.itemStatus === JobStatus.Filled)
    : canEditJob && isUnfilledOrDraft && isFutureJob;

  const descriptionWithoutQuestionnaire = useMemo(
    () => stripQuestionnaireDescriptionAddendum(job.description),
    [job.description],
  );

  const scheduleStartDay = getJobDisplayScheduleStartDay(job);
  const scheduleEndDay = getJobDisplayScheduleEndDay(job);
  const workingDaysBadges =
    scheduleStartDay && scheduleEndDay
      ? getWorkingDaysBadgesForJob(job, scheduleStartDay, scheduleEndDay)
      : [];

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <JobCancelInfo job={job} showWorkerLink={showWorkerLink} />
      <div className="mb-6">
        {job.userId ? (
          <div className="max-w-100 rounded-lg bg-gray-100 p-4">
            <AssignedWorker
              job={job}
              allowUnassign={
                canAssignWorker && job.itemStatus !== JobStatus.Cancelled
              }
              showWorkerPayRate={canAssignWorker}
              showWorkerLink={showWorkerLink}
              showAssignedCandidateEmail={showAssignedCandidateEmail}
              showAssignedCandidatePhone={showAssignedCandidatePhone}
              onUnassigned={onWorkerUnassigned}
            />
          </div>
        ) : (
          canAssignWorker && (
            <div className="max-w-100">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">
                {job.workersShouldApplyToJob
                  ? getTerm(TemplateTerm.WORKER, true)
                  : getTerm(TemplateTerm.WORKER, false)}
              </h3>
              {isAssigningWorker ? (
                <div className="flex h-12 w-full items-center rounded-md border border-gray-200 bg-white p-2">
                  <div className="pl-3 text-sm text-gray-600">Assigning...</div>
                </div>
              ) : (
                <WorkerSearchbar
                  job={job}
                  setLoading={setIsAssigningWorker}
                  onAssigned={onWorkerAssigned}
                  showResultsOnFocus
                />
              )}
            </div>
          )
        )}
      </div>

      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Job Information</h2>
        {showEditButton && (
          <Button
            type="button"
            variant="text"
            onClick={onEdit}
            disabled={!showEditButton}
            className="!h-auto !min-h-0 !w-auto !justify-center !px-0 !py-0 !text-sm !font-medium !text-blue-600 hover:!text-blue-700"
          >
            Edit
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900">
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${
                    job?.itemStatus === JobStatus.Filled
                      ? 'bg-green-100 text-green-800'
                      : job?.itemStatus === JobStatus.Unfilled
                        ? 'bg-yellow-100 text-yellow-800'
                        : job?.itemStatus === JobStatus.Cancelled
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {job.itemStatus || 'Unknown'}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm whitespace-pre-line text-gray-900">
                {descriptionWithoutQuestionnaire || 'No description provided'}
              </dd>
            </div>
            {job.customAttributes && job.customAttributes.length > 0 && (
              <QuestionnaireResponses
                customAttributes={job.customAttributes}
                questions={questionnaireQuestions}
              />
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Start date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {`${getJobStartTimeString12h(job)}, ${formatDate(job.startDate || '')}`}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">End date</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {`${getJobEndTimeString12h(job)}, ${formatDate(job.endDate || '')}`}
              </dd>
            </div>
            {workingDaysBadges.length > 0 && (
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  Working days
                </dt>
                <dd className="mt-1">
                  <WorkingDaysDisplay badges={workingDaysBadges} />
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div>
          {job.addressHash ? (
            <dl className="space-y-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Address</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {job.address?.addressFriendly}
                </dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-gray-500">
              No location information available
            </p>
          )}
        </div>
      </div>

      {CANCELLABLE_STATUSES.includes(job?.itemStatus as JobStatus) &&
        !isJobInPast(job) && (
          <div className="mt-6 border-t border-gray-200 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCancelModalOpen(true)}
              className="!rounded-lg !border-red-600 !bg-red-50 !py-2 !text-sm !text-red-700 hover:!border-red-600 hover:!bg-red-100"
            >
              Cancel job
            </Button>
          </div>
        )}

      <CancelJobModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        job={job}
        onSuccess={() => {
          onCancelSuccess?.();
          toast.success('Job cancelled successfully', {
            position: 'bottom-left',
            autoClose: 3000,
          });
        }}
      />
    </div>
  );
};

export default JobDetails;
